import Button from '../common/Button.js';
import SettlementModalShell from '../common/SettlementModalShell.js';
import { formatCurrencyPaise } from '../../utils/formatCurrency.js';

const money = (v) => formatCurrencyPaise(v, '₹', { alwaysPaise: true });

export default function CashbookTransferPreviewModal({
  open,
  preview,
  loading,
  openDuesLoading = false,
  onClose,
  onConfirm,
  confirming,
}) {
  if (!open) return null;

  const p = preview || {};
  const openDues = p.open_agent_dues || [];
  const settled = p.settled_to_ho_today || [];
  const branchIns = p.branch_transfers_in || [];

  return (
    <SettlementModalShell
      title="Transfer to admin branch"
      subtitle={p.from_label && p.to_label ? `${p.from_label} → ${p.to_label}` : ''}
      onClose={onClose}
      size="md"
      footer={(
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={confirming}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={onConfirm}
            loading={confirming}
            disabled={confirming || loading || !p.can_transfer}
          >
            Confirm transfer
          </Button>
        </div>
      )}
    >
      {loading ? (
        <p className="text-sm text-gray-500 py-6 text-center">Loading preview…</p>
      ) : (
        <div className="space-y-4 text-sm text-gray-800">
          <div className="rounded-xl border border-sky-100 bg-sky-50/70 px-4 py-3">
            <p className="text-xs text-gray-500">Moving out (cash + UPI)</p>
            <p className="mt-1 text-lg font-semibold tabular-nums text-sky-950">{money(p.total)}</p>
            <p className="mt-1 text-xs text-gray-600">
              Cash {money(p.cash)} · UPI {money(p.upi)}
            </p>
            <p className="mt-2 text-xs text-gray-500">
              Branch balance after transfer: {money(p.balance_after ?? 0)}
            </p>
          </div>

          {branchIns.length ? (
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Already received from other branches today</p>
              <ul className="mt-2 space-y-1">
                {branchIns.map((row) => (
                  <li key={row.label} className="flex justify-between gap-3 tabular-nums">
                    <span className="truncate">{row.label}</span>
                    <span>{money(row.total)}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-1 text-xs text-gray-500">
                Local collections in this sweep (approx.): {money(p.local_collections_total)}
              </p>
            </div>
          ) : null}

          {settled.length ? (
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Settlements to HO recorded today</p>
              <ul className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                {settled.map((row) => (
                  <li key={`${row.request_id}-${row.kind}`} className="flex justify-between gap-3 tabular-nums">
                    <span className="truncate">{row.agent_name} · {row.kind}</span>
                    <span>{money(row.amount)}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-1 text-xs text-gray-500">Total {money(p.settled_to_ho_today_total)}</p>
            </div>
          ) : null}

          {openDuesLoading ? (
            <div className="rounded-xl border border-amber-100 bg-amber-50/60 px-4 py-3">
              <p className="text-xs font-medium text-amber-900">Not moved by this transfer (open agent dues)</p>
              <p className="mt-2 text-xs text-amber-800">Loading agent dues…</p>
            </div>
          ) : openDues.length ? (
            <div className="rounded-xl border border-amber-100 bg-amber-50/60 px-4 py-3">
              <p className="text-xs font-medium text-amber-900">Not moved by this transfer (open agent dues)</p>
              <ul className="mt-2 space-y-1 max-h-28 overflow-y-auto">
                {openDues.map((row) => (
                  <li key={row.user_id} className="flex justify-between gap-3 tabular-nums text-amber-950">
                    <span className="truncate">{row.name}</span>
                    <span>{money(row.amount_due)}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-1 text-xs text-amber-800">
                After a sweep, new settlements to HO post at HO only — they will not debit this branch again.
              </p>
            </div>
          ) : (
            <p className="text-xs text-gray-500">
              After a sweep, new settlements to HO post at HO only — they will not debit this branch again.
            </p>
          )}

          {!p.can_transfer ? (
            <p className="text-sm text-red-700">This branch has no balance to transfer.</p>
          ) : null}
        </div>
      )}
    </SettlementModalShell>
  );
}
